import React from 'react';
import { Check } from 'lucide-react';
import { RESERVED_DOMAIN_BY_KEY, reservedDomainLabel } from './reservedDomains';
import { plainMentions } from './structure/focus';

// Gouvernance : vocabulaire, lecture des changements proposés et statut d'une tension (partagés par l'éditeur de
// tension, les votes et la réunion de gouvernance).

export const ACTION_LABELS = { edit: 'Modifier', create: 'Créer', delete: 'Supprimer' };

export const FIELD_LABELS = {
  name: 'Nom',
  purpose: "Raison d'être",
  domains: 'Domaines',
  accountabilities: 'Redevabilités',
  reserved_domains: 'Domaines à effet',
};

const TEXT_FIELDS = ['name', 'purpose'];

export const sameId = (a, b) =>
  a !== null && a !== undefined && a !== '' && b !== null && b !== undefined && b !== '' && String(a) === String(b);

export const findCircle = (circles, id) => (circles || []).find((c) => sameId(c?.id, id)) || null;

export const snapshotOf = (circle) => ({
  name: circle?.name || '',
  purpose: circle?.purpose || '',
  domains: circle?.domains || [],
  accountabilities: circle?.accountabilities || [],
  reserved_domains: circle?.reserved_domains || [],
  parent_id: circle?.parent_id ?? null,
});

export const parseOperations = (raw) => {
  let ops = raw;
  if (typeof ops === 'string') {
    try {
      ops = JSON.parse(ops);
    } catch (err) {
      ops = [];
    }
  }
  return Array.isArray(ops) ? ops.filter((op) => op && typeof op === 'object') : [];
};

// Un changement vierge : la cible d'une modification ou d'une suppression se choisit ensuite.
export const buildOperation = (action = 'edit', circleId = null) => {
  const circle = circleId ? Number(circleId) : null;
  if (action === 'create') {
    return {
      action: 'create',
      circle_id: circle,
      target_circle_id: null,
      data: { name: '', purpose: '', domains: [], accountabilities: [], reserved_domains: [], parent_id: circle },
      original_data: {},
    };
  }
  return { action, circle_id: circle, target_circle_id: null, data: {}, original_data: {} };
};

// Ce qui manque pour qu'un changement soit applicable (null si rien).
export const operationIssue = (op) => {
  const action = op?.action || 'edit';
  if (action === 'create') return (op?.data?.name || '').trim() ? null : 'Donne un nom au nouveau rôle.';
  if (!op?.target_circle_id) {
    return action === 'delete' ? 'Choisis le rôle à supprimer.' : 'Choisis le rôle ou cercle à modifier.';
  }
  return null;
};

const normText = (value) => String(value ?? '').trim();
const normList = (value) => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return list.map((item) => String(item ?? '').trim()).filter(Boolean);
};

// État « avant » : ce qu'a vu l'auteur (original_data), complété par le rôle tel qu'il est aujourd'hui.
export const baselineOf = (op, circles) => {
  if ((op?.action || 'edit') === 'create') return {};
  const live = findCircle(circles, op?.target_circle_id);
  const base = live ? snapshotOf(live) : {};
  const original = op?.original_data || {};
  Object.keys(original).forEach((key) => {
    if (original[key] !== undefined && original[key] !== null) base[key] = original[key];
  });
  return base;
};

// Valeur affichée dans un formulaire : la proposition si le champ y est, sinon l'état de départ.
export const effectiveData = (op, baseline) => {
  const data = op?.data || {};
  const pick = (key, fallback) => (data[key] !== undefined && data[key] !== null ? data[key] : (baseline?.[key] ?? fallback));
  return {
    name: pick('name', ''),
    purpose: pick('purpose', ''),
    domains: pick('domains', []),
    accountabilities: pick('accountabilities', []),
    reserved_domains: pick('reserved_domains', []),
  };
};

// Domaines d'un rôle en une seule liste : ceux à effet (🔑, clés de reserved_domains) puis ceux écrits librement.
const domainItems = (domains, reserved) => [
  ...normList(reserved)
    .filter((key) => RESERVED_DOMAIN_BY_KEY[key])
    .map((key) => ({ id: `effect:${key}`, text: reservedDomainLabel(key), effect: true })),
  ...normList(domains).map((text) => ({ id: `text:${text}`, text, effect: false })),
];

const listChange = (key, label, kind, before, after, idOf) => {
  const beforeIds = new Set(before.map(idOf));
  const afterIds = new Set(after.map(idOf));
  const added = after.filter((item) => !beforeIds.has(idOf(item)));
  const removed = before.filter((item) => !afterIds.has(idOf(item)));
  if (added.length === 0 && removed.length === 0) return null;
  return { key, label, kind, before, after, added, removed };
};

// Champs réellement changés par un changement « Modifier » ou « Créer ». Les domaines (libres et à effet) forment
// un seul changement « Domaines ».
export const operationChanges = (op, circles) => {
  const action = op?.action || 'edit';
  if (action === 'delete') return [];
  const data = op?.data || {};
  const base = baselineOf(op, circles);
  const changes = [];
  TEXT_FIELDS.forEach((key) => {
    if (data[key] === undefined || data[key] === null) return;
    const after = normText(data[key]);
    const before = normText(base[key]);
    if (action === 'create' ? !after : after === before) return;
    changes.push({ key, label: FIELD_LABELS[key], kind: 'text', before, after });
  });
  const has = (key) => data[key] !== undefined && data[key] !== null;
  if (has('domains') || has('reserved_domains')) {
    const before = domainItems(base.domains, base.reserved_domains);
    const after = domainItems(has('domains') ? data.domains : base.domains,
      has('reserved_domains') ? data.reserved_domains : base.reserved_domains);
    const change = listChange('domains', FIELD_LABELS.domains, 'domains', before, after, (item) => item.id);
    if (change) changes.push(change);
  }
  if (has('accountabilities')) {
    const change = listChange('accountabilities', FIELD_LABELS.accountabilities, 'list',
      normList(base.accountabilities), normList(data.accountabilities), (item) => item);
    if (change) changes.push(change);
  }
  return changes;
};

// Texte affiché d'un élément : les mentions « @[role:14|Pilote client] » deviennent « @Pilote client ».
export const formatItem = (change, item) => plainMentions(change.kind === 'domains' ? item.text : item);
export const itemKey = (change, item) => (change.kind === 'domains' ? item.id : String(item));

// En-tête lisible : verbe, élément visé et, pour une création, le cercle d'accueil.
export const operationTitle = (op, circles) => {
  const action = op?.action || 'edit';
  if (action === 'create') {
    const parent = findCircle(circles, op?.data?.parent_id ?? op?.circle_id);
    return { action, verb: ACTION_LABELS.create, name: normText(op?.data?.name) || 'Nouveau rôle', where: parent?.name || null };
  }
  const target = findCircle(circles, op?.target_circle_id);
  const name = target?.name
    || op?.original_data?.name
    || (op?.target_circle_id ? "Rôle qui n'existe plus" : 'Rôle à choisir');
  return { action, verb: ACTION_LABELS[action] || ACTION_LABELS.edit, name, where: null };
};

export const describeOperation = (op, circles) => {
  const title = operationTitle(op, circles);
  if (title.action === 'create') {
    return `Créer « ${title.name} »${title.where ? ` dans « ${title.where} »` : ''}`;
  }
  if (title.action === 'delete') return `Supprimer « ${title.name} »`;
  const changes = operationChanges(op, circles);
  if (changes.length === 0) return `Modifier « ${title.name} » (aucun changement de contenu)`;
  return `Modifier « ${title.name} » : ${changes.map((c) => c.label.toLowerCase()).join(', ')}`;
};

// Clés stables pour une liste éditable : supprimer une ligne ne fait pas « glisser » la saisie des suivantes.
export const useStableKeys = (length) => {
  const keysRef = React.useRef([]);
  const counterRef = React.useRef(0);
  const nextKey = () => {
    counterRef.current += 1;
    return `item-${counterRef.current}`;
  };
  if (keysRef.current.length !== length) {
    const keys = keysRef.current.slice(0, length);
    while (keys.length < length) keys.push(nextKey());
    keysRef.current = keys;
  }
  return {
    keys: keysRef.current,
    remove: (index) => {
      keysRef.current = keysRef.current.filter((_, i) => i !== index);
    },
    append: () => {
      keysRef.current = [...keysRef.current, nextKey()];
    },
  };
};

// Pluriel simple : plural(3, 'ajout') → « 3 ajouts ».
export const plural = (count, word) => `${count} ${word}${count > 1 ? 's' : ''}`;

export const formatDate = (value, withTime = true) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

const TONES = {
  slate: 'bg-slate-100 text-slate-600 border-slate-200',
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  purple: 'bg-purple-50 text-purple-700 border-purple-100',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-100',
};

// Statut en clair d'une tension : Brouillon, En réunion, Au vote (n/m votes), Appliquée, Refusée…
export const tensionStatus = (tension) => {
  const status = String(tension?.status || 'draft').toLowerCase();
  if (status === 'applied') return { key: 'applied', step: 3, label: 'Appliquée', tone: 'emerald' };
  if (status === 'meeting') {
    return tension?.decision_error
      ? { key: 'meeting', step: 2, label: 'En réunion — à corriger', tone: 'red' }
      : { key: 'meeting', step: 2, label: 'En réunion', tone: 'indigo' };
  }
  if (status === 'proposal') {
    const total = Number(tension?.votes_total || 0);
    const cast = Number(tension?.votes_cast || 0);
    return { key: 'proposal', step: 2, label: total > 0 ? `Au vote (${cast}/${plural(total, 'vote')})` : 'Au vote', tone: 'purple' };
  }
  if (status.startsWith('reject')) return { key: 'rejected', step: 3, label: 'Refusée en réunion', tone: 'red' };
  if (tension?.decision_error) return { key: 'failed', step: 1, label: 'Non appliquée — à corriger', tone: 'red' };
  if (tension?.rejected_at) return { key: 'reworked', step: 1, label: 'Refusée — à retravailler', tone: 'amber' };
  return { key: 'draft', step: 1, label: 'Brouillon', tone: 'slate' };
};

export const StatusChip = ({ tension, className = '' }) => {
  const status = tensionStatus(tension);
  return (
    <span className={`inline-flex items-center whitespace-nowrap text-[10px] font-semibold px-2 py-0.5 rounded-full border ${TONES[status.tone]} ${className}`}>
      {status.label}
    </span>
  );
};

const STEPS = [
  { step: 1, title: 'Rédiger', detail: 'Ce qui coince et la proposition' },
  { step: 2, title: 'Proposer', detail: 'Au vote ou en réunion de gouvernance' },
  { step: 3, title: 'Décision', detail: 'Acceptée : la structure est mise à jour' },
];

// Les trois étapes d'une tension, l'étape courante mise en avant.
export const GovernanceSteps = ({ tension, className = '' }) => {
  const status = tensionStatus(tension);
  const current = status.key === 'applied' || status.key === 'rejected' ? 4 : status.step;
  return (
    <ol className={`grid grid-cols-3 gap-2 ${className}`}>
      {STEPS.map(({ step, title, detail }) => {
        const done = step < current;
        const active = step === current;
        return (
          <li
            key={step}
            className={`rounded-xl border px-3 py-2 ${active ? 'border-indigo-200 bg-indigo-50' : done ? 'border-emerald-100 bg-emerald-50/60' : 'border-slate-200 bg-white'}`}
          >
            <p className={`flex items-center gap-1.5 text-xs font-semibold ${active ? 'text-indigo-700' : done ? 'text-emerald-700' : 'text-slate-500'}`}>
              <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${active ? 'bg-indigo-600 text-white' : done ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                {done ? <Check size={10} /> : step}
              </span>
              {title}
            </p>
            <p className="mt-0.5 text-[11px] leading-snug text-slate-500">{detail}</p>
          </li>
        );
      })}
    </ol>
  );
};
